import {AttrsSchema, Component} from 'panel';

import {workspacesClient} from 'common/report/queries/app-api/workspaces-client';
import {userDetailsStore} from 'common/report/stores/user-details';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {toTitleCase} from 'common/util/string';
import {getMpContext} from 'common/mp-context';
import {getReportUrl} from 'common/report/util';
import {
  WorkspaceGlobalOnlyBannerAttrs,
  WorkspaceGlobalOnlyBannerState,
  WorkspaceStatus,
} from 'common/widgets/workspace-global-only-banner/types';

import {template} from './template';
import css from './index.styl?inline';

const AppNameUrlMap = {
  'a/b testing': `ab_test`,
  journeys: `journeys`,
  integrations: `integrations`,
  messages: `triggers/messages`,
  campaign: `triggers/campaign`,
  signal: `signal`,
  lexicon: `lexicon#audit`,
};

interface WorkspaceGlobalOnlyBannerJsxProps {
  attrs?: WorkspaceGlobalOnlyBannerAttrs;
}

export class WorkspaceGlobalOnlyBanner extends Component<
  WorkspaceGlobalOnlyBannerState,
  WorkspaceGlobalOnlyBannerAttrs
> {
  static get attrsSchema(): AttrsSchema<WorkspaceGlobalOnlyBannerAttrs> {
    return {
      ...super.attrsSchema,
      'workspace-id': `number`,
      'app-name': `string`,
    };
  }

  constructor() {
    super();
    userDetailsStore.subscribe(() => this.userDetailsUpdate()).fetch();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        workspaceStatus: WorkspaceStatus.LOADING,
        projectId: null,
        globalProjectWorkspace: {
          name: ``,
          id: null,
        },
      },
      helpers: {
        getAppPath: (): string => {
          return AppNameUrlMap[this.attr(`app-name`)];
        },
        getIsOrAre: (): string => {
          const plurals = [`messages`, `journeys`, `integrations`];
          if (plurals.includes(this.attr(`app-name`))) return `are`;
          return `is`;
        },
        getAppName: (): string => {
          if (this.attr(`app-name`) === `a/b testing`) {
            return `A/B Testing`;
          } else if (this.attr(`app-name`) === `lexicon`) {
            return `Data Audit`;
          }
          return toTitleCase(this.attr(`app-name`));
        },
        getProjectId: (): number => {
          return this.state.projectId;
        },
        getAppUrl: ({
          appPath,
          projectId,
          workspaceId,
        }: {
          appPath: string;
          projectId?: number;
          workspaceId?: number;
        }): string => {
          return getReportUrl(appPath, {projectId, workspaceId});
        },
      },
    };
  }

  userDetailsUpdate() {
    const details = userDetailsStore.value;
    const workspaceId = this.attr(`workspace-id`);
    let status = WorkspaceStatus.PROJECT;
    let globalProjectWorkspace = {name: ``, id: null};
    let projectId = null;

    if (workspaceId) {
      status = WorkspaceStatus.LOADING_WORKSPACE;
      const workspace = details.workspaces[workspaceId];
      if (workspace?.isGlobal) {
        status = WorkspaceStatus.GLOBAL_WORKSPACE;
      } else {
        projectId = workspace ? workspace.projectId : getMpContext().projectId;
        const foundGlobalProjectWorkspace = Object.values(details.workspaces)
          .filter((w) => w.projectId === projectId)
          .find((w) => w.isGlobal);

        // see if the global workspace[dataview] is public for instructing the user to join
        if (!foundGlobalProjectWorkspace) {
          workspacesClient.getPublicWorkspaces(projectId).then((publicWorkspaces) => {
            const publicGlobalProjectWorkspace = publicWorkspaces.find((w) => w.isGlobal);
            this.update({globalProjectWorkspace: publicGlobalProjectWorkspace});
          });
        }

        status = foundGlobalProjectWorkspace
          ? WorkspaceStatus.HAS_GLOBAL_WORKSPACE_ACCESS
          : WorkspaceStatus.NO_WORKSPACE_ACCESS;
        globalProjectWorkspace = foundGlobalProjectWorkspace;
      }
    }
    this.update({workspaceStatus: status, globalProjectWorkspace, projectId});
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === `workspace-id`) {
      this.userDetailsUpdate();
    }
  }
}

export const WorkspaceGlobalOnlyBannerEl = defineAndMakeJsxFactory<WorkspaceGlobalOnlyBannerJsxProps>(
  `workspace-global-only-banner`,
  WorkspaceGlobalOnlyBanner,
);
